import { skipToken } from "@reduxjs/toolkit/query";
import NotificationBell from "components/notifications/NotificationBell";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tokenStorage } from "../../config/api";
import { useGetProfileQuery, useLogoutMutation } from "../../store/api/authApi";
import Icon from "../AppIcon";
import Button from "../ui/Button";
import Loader from "../ui/Loader";

const TopBar: React.FC = () => {
	const navigate = useNavigate();
	const [isProfileOpen, setIsProfileOpen] = useState(false);
	const profileRef = useRef<HTMLDivElement | null>(null);
	// Only fetch profile if user has a token (skip query if no token to avoid 403 errors)
	const accessToken = tokenStorage.getAccessToken();
	const { data: user, isLoading } = useGetProfileQuery(
		accessToken ? undefined : skipToken,
	);
	const [logout] = useLogoutMutation();

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				profileRef.current &&
				!profileRef.current.contains(event.target as Node)
			) {
				setIsProfileOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const handleLogout = async () => {
		try {
			await logout().unwrap();
			tokenStorage.clearTokens();
			navigate("/login");
		} catch (error) {
			console.error("Logout failed:", error);
			// Clear tokens anyway and redirect
			tokenStorage.clearTokens();
			navigate("/login");
		}
	};

	// Only render profile section when user data is available
	if (isLoading || !user) {
		return (
			<header className="fixed top-0 right-0 left-0 lg:left-60 h-16 bg-card border-b border-border items-center justify-between px-6 z-1000 hidden lg:flex">
				<div className="flex items-center space-x-2">
					<button
						onClick={() => navigate("/")}
						className="flex items-center space-x-2 hover:opacity-80 smooth-transition"
					>
						<Icon name="ArrowLeft" size={10} className="text-foreground" />
						<span className="text-sm font-medium text-foreground">
							Back to Home
						</span>
					</button>
				</div>
				<div className="flex items-center space-x-4">
					<NotificationBell />
					<Loader size="sm" />
				</div>
			</header>
		);
	}

	const userName = `${user.first_name} ${user.last_name}`;
	const userEmail = user.email;

	return (
		<header className="fixed top-0 right-0 left-0 lg:left-60 h-16 bg-card border-b border-border items-center justify-between px-6 z-1000 hidden lg:flex">
			<div className="flex items-center space-x-2">
				<button
					onClick={() => navigate("/")}
					className="flex items-center space-x-2 hover:opacity-80 smooth-transition"
				>
					<Icon name="ArrowLeft" size={10} className="text-foreground" />
					<span className="text-sm font-medium text-foreground">
						Back to Home
					</span>
				</button>
			</div>

			<div className="flex items-center space-x-4">
				{/* Notifications */}
				<NotificationBell />

				{/* User Profile Dropdown */}
				<div className="relative" ref={profileRef}>
					<Button
						variant="ghost"
						onClick={() => setIsProfileOpen(!isProfileOpen)}
						className="flex items-center space-x-2 px-3"
					>
						<div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center">
							{user?.avatar ? (
								<img
									src={user.avatar}
									alt={userName}
									className="w-full h-full rounded-full object-cover"
								/>
							) : (
								<Icon name="User" size={16} />
							)}
						</div>
						<span className="text-sm font-medium text-foreground">
							{userName}
						</span>
						<Icon
							name="ChevronDown"
							size={16}
							className={`smooth-transition ${
								isProfileOpen ? "rotate-180" : ""
							}`}
						/>
					</Button>

					{isProfileOpen && (
						<div className="absolute right-0 top-12 w-56 bg-popover border border-border rounded-lg modal-shadow z-[90] animate-scale-in">
							<div className="p-4 border-b border-border">
								<p className="font-medium text-popover-foreground">
									{userName}
								</p>
								<p className="text-sm text-muted-foreground">{userEmail}</p>
								<span className="inline-block mt-2 px-2 py-1 bg-primary/10 text-primary text-xs rounded-full capitalize">
									{user.role}
								</span>
							</div>

							<div className="py-2">
								<button
									onClick={() => {
										setIsProfileOpen(false);
										navigate("/property-viewing-management");
									}}
									className="w-full px-4 py-2 text-left text-sm text-popover-foreground hover:bg-muted smooth-transition flex items-center space-x-2"
								>
									<Icon name="Calendar" size={16} />
									<span>Viewing Management</span>
								</button>
								<button
									onClick={() => {
										setIsProfileOpen(false);
										navigate("/sales-management");
									}}
									className="w-full px-4 py-2 text-left text-sm text-popover-foreground hover:bg-muted smooth-transition flex items-center space-x-2"
								>
									<Icon name="TrendingUp" size={16} />
									<span>Sales Management</span>
								</button>
								<div className="border-t border-border my-2" />
								<button
									onClick={() => {
										setIsProfileOpen(false);
										navigate("/profile");
									}}
									className="w-full px-4 py-2 text-left text-sm text-popover-foreground hover:bg-muted smooth-transition flex items-center space-x-2"
								>
									<Icon name="User" size={16} />
									<span>Profile Settings</span>
								</button>
								<button
									onClick={() => {
										setIsProfileOpen(false);
										navigate("/settings");
									}}
									className="w-full px-4 py-2 text-left text-sm text-popover-foreground hover:bg-muted smooth-transition flex items-center space-x-2"
								>
									<Icon name="Settings" size={16} />
									<span>Account Settings</span>
								</button>
								<button
									onClick={() => {
										setIsProfileOpen(false);
										navigate("/help");
									}}
									className="w-full px-4 py-2 text-left text-sm text-popover-foreground hover:bg-muted smooth-transition flex items-center space-x-2"
								>
									<Icon name="HelpCircle" size={16} />
									<span>Help & Support</span>
								</button>
							</div>

							<div className="border-t border-border py-2">
								<button
									onClick={handleLogout}
									className="w-full px-4 py-2 text-left text-sm text-error hover:bg-error/10 smooth-transition flex items-center space-x-2"
								>
									<Icon name="LogOut" size={16} />
									<span>Sign Out</span>
								</button>
							</div>
						</div>
					)}
				</div>
			</div>
		</header>
	);
};

export default TopBar;
